<div class="relative flex h-full min-h-0 w-full flex-col !items-stretch overflow-hidden bg-slate-50 text-gray-900">
  <div class="flex w-full shrink-0 items-center gap-3 border-b border-gray-100 bg-white px-4 py-3">
    @if (task) {
      <span class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-orange-50 text-orange-600">
        <app-icon>task_alt</app-icon>
      </span>
    }
    <div class="min-w-0 flex-1">
      <div class="text-xs font-semibold uppercase tracking-wide text-gray-400">
        {{ 'crm_tasks.detail.title' | translate }}
      </div>
      @if (task) {
        <div class="truncate text-lg font-bold leading-6 text-gray-900" [title]="displayTitle">
          {{ displayTitle || '-' }}
        </div>
      } @else {
        <div class="mt-1 h-6 w-48 animate-pulse rounded bg-gray-200"></div>
      }
    </div>
    @if (task) {
      <div class="flex shrink-0 items-center gap-1.5">
        <app-task-status-badge [status]="task.status" />
        @if (task.is_overdue) {
          <span
            class="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700"
          >
            <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">alarm</app-icon>
            {{ 'crm_tasks.overdue' | translate }}
          </span>
        }
      </div>
      <button
        type="button"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-rose-50 hover:text-rose-600"
        [matTooltip]="'delete' | translate"
        [disabled]="busy || saving"
        (click)="remove()"
      >
        <app-icon>delete</app-icon>
      </button>
    }
    <button
      type="button"
      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
      [attr.aria-label]="'close' | translate"
      (click)="requestClose()"
    >
      <app-icon>close</app-icon>
    </button>
  </div>

  @if (task) {
    <div class="flex w-full shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2.5">
      @for (action of statusActions; track action.status) {
        <button
          type="button"
          class="inline-flex h-9 items-center gap-1.5 !rounded-lg px-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
          [ngClass]="actionClasses[action.tone]"
          [disabled]="busy || saving"
          (click)="changeStatus(action)"
        >
          <app-icon class="!h-[18px] !w-[18px] !text-[18px] !leading-none">{{ action.icon }}</app-icon>
          {{ action.labelKey | translate }}
        </button>
      }
      @if (task.status === 'REVIEW' && !isCreator) {
        <span class="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700">
          <app-icon class="!h-[18px] !w-[18px] !text-[18px] !leading-none">hourglass_top</app-icon>
          {{ 'crm_tasks.detail.waiting_review' | translate }}
        </span>
      }
    </div>
  }

  <div class="crm-scroll min-h-0 w-full flex-1 overflow-y-auto overflow-x-hidden p-4">
    @if (loading) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div class="h-80 animate-pulse rounded-lg bg-white"></div>
        <div class="h-80 animate-pulse rounded-lg bg-white"></div>
      </div>
    } @else if (task) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(320px,0.42fr)_minmax(420px,0.58fr)]">
        <div class="flex min-w-0 flex-col gap-4">
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="mb-3 border-b border-slate-100 pb-3 text-base font-semibold text-slate-950">
              {{ 'crm_tasks.detail.info' | translate }}
            </div>
            <app-task-fields
              [form]="form"
              [users]="users"
              [canLink]="canLink"
              [openableLinks]="true"
              [canOpenCards]="canOpenCards"
              (openLink)="openLink($event)"
            />
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="mb-3 border-b border-slate-100 pb-3 text-base font-semibold text-slate-950">
              {{ 'crm_tasks.fields.reminders' | translate }}
            </div>
            <app-task-reminders
              [reminders]="reminders"
              [hasDeadline]="!!task.deadline"
              [busy]="busy || saving"
              (add)="addReminder($event)"
              (remove)="removeReminder($event)"
            />
          </section>
        </div>

        <div class="flex min-w-0 flex-col gap-4">
          <section
            class="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-3"
          >
            <div class="grid min-w-0 gap-1">
              <div class="text-xs font-medium text-slate-500">{{ 'crm_tasks.fields.creator' | translate }}</div>
              <app-task-user class="text-sm font-medium text-slate-800" [user]="creator" />
            </div>
            <div class="grid min-w-0 gap-1">
              <div class="text-xs font-medium text-slate-500">{{ 'crm_tasks.fields.created_at' | translate }}</div>
              <div class="text-sm font-medium text-slate-800">{{ task.created_at | date: 'dd.MM.yyyy HH:mm' }}</div>
            </div>
            <div class="grid min-w-0 gap-1">
              <div class="text-xs font-medium text-slate-500">{{ 'crm_tasks.fields.updated_at' | translate }}</div>
              <div class="text-sm font-medium text-slate-800">
                {{ task.updated_at ? (task.updated_at | date: 'dd.MM.yyyy HH:mm') : '—' }}
              </div>
            </div>
          </section>

          <app-task-timeline
            [taskId]="task.id"
            [events]="timelines"
            [loading]="timelineLoading"
            (changed)="loadTimeline()"
          />
        </div>
      </div>
    }
  </div>

  <div
    class="w-full shrink-0 border-t border-slate-200 bg-white px-5 py-3 shadow-[0_-4px_12px_rgba(15,23,42,0.04)]"
    [class.hidden]="!task"
  >
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex min-h-10 items-center gap-2 text-sm">
        @if (hasChanges) {
          <span class="h-2 w-2 shrink-0 rounded-full bg-amber-400"></span>
          <span class="font-medium text-amber-700">{{ 'unsaved_changes' | translate }}</span>
        } @else {
          <span class="h-2 w-2 shrink-0 rounded-full bg-emerald-500"></span>
          <span class="font-medium text-slate-500">{{ 'changes_saved' | translate }}</span>
        }
      </div>

      <div class="flex shrink-0 gap-2">
        <app-button
          btnType="stroked"
          styleType="rounded"
          [customClass]="'!h-10 min-w-[132px] !rounded-lg !px-5 text-sm font-semibold !leading-none [&.btn-stroked.btn-stroked]:!border-slate-950 [&.btn-stroked.btn-stroked]:!text-slate-950 hover:[&.btn-stroked.btn-stroked]:!bg-slate-50 active:[&.btn-stroked.btn-stroked]:!bg-slate-100 [--mat-button-outlined-hover-state-layer-opacity:0] [--mat-button-outlined-focus-state-layer-opacity:0] [--mat-button-outlined-pressed-state-layer-opacity:0] [&.btn-stroked.btn-stroked]:disabled:!border-slate-200 [&.btn-stroked.btn-stroked]:disabled:!bg-white [&.btn-stroked.btn-stroked]:disabled:!text-slate-300'"
          [disabled]="saving || !hasChanges"
          (onChangeClick)="cancelChanges()"
        >
          {{ 'cancel' | translate }}
        </app-button>
        <app-button
          styleType="rounded"
          [customClass]="'!h-10 min-w-[132px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0] [&.btn-default.btn-default]:disabled:!bg-slate-200 [&.btn-default.btn-default]:disabled:!text-slate-400'"
          [disabled]="saving || busy || !hasChanges"
          (onChangeClick)="save()"
        >
          {{ (saving ? 'saving' : 'save') | translate }}
        </app-button>
      </div>
    </div>
  </div>

  @if (showCloseConfirmation) {
    <div class="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/35 p-5">
      <div class="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-2xl">
        <div class="flex items-start gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">edit</app-icon>
          </div>
          <div class="min-w-0">
            <div class="text-base font-semibold text-slate-950">{{ 'unsaved_changes' | translate }}</div>
            <p class="mb-0 mt-1 text-sm leading-5 text-slate-600">
              {{ 'crm_tasks.detail.save_before_closing' | translate }}
            </p>
          </div>
        </div>

        <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <app-button
            btnType="stroked"
            styleType="rounded"
            [customClass]="'!h-11 min-w-[132px] !rounded-lg !px-5 text-sm font-semibold !leading-none'"
            [disabled]="saving"
            (onChangeClick)="cancelClose()"
          >
            {{ 'cancel' | translate }}
          </app-button>
          <app-button
            btnType="cancel"
            styleType="rounded"
            [customClass]="'!h-11 min-w-[148px] !rounded-lg !bg-rose-600 !px-5 text-sm font-semibold !leading-none hover:!bg-rose-700 active:!bg-rose-800'"
            [disabled]="saving"
            (onChangeClick)="discardChangesAndClose()"
          >
            {{ 'dont_save' | translate }}
          </app-button>
          <app-button
            styleType="rounded"
            [customClass]="'!h-11 min-w-[132px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800'"
            [disabled]="saving || busy"
            (onChangeClick)="save(true)"
          >
            {{ (saving ? 'saving' : 'save') | translate }}
          </app-button>
        </div>
      </div>
    </div>
  }
</div>
